import { Helmet } from "react-helmet";
import Container from "../../components/Container";
import React, { useState } from "react";
import pb from "../../lib/pocketbase";
import { Input } from "../../components/ui/input";
import { useForm } from "react-hook-form";
import Button from "../../components/form/button";
import { Status } from "../../lib/types";
import ErrorMessage from "../../components/form/errorMessage";
import { ClientResponseError } from "pocketbase";
import { useToast } from "../../components/ui/use-toast";
import { DatePicker } from "../../components/ui/datepicker";
import Confirmation from "../../components/form/Confirmation";
import ChangeEmail from "../../components/studio/settings/ChangeEmail";

interface SettingsData {
    birthday: Date;
    username: string;
    name: string;
    avatar: File;
    banner: File;
}

export default function Settings() {
    const { register, handleSubmit, setValue } = useForm<SettingsData>();
    const { toast } = useToast();
    const [status, setStatus] = useState<Status>("none");
    const [error, setError] = useState<string>("");

    function Save(data: SettingsData) {
        setStatus("loading");

        const fd = new FormData();
        fd.append("birthday", data.birthday.toString());
        fd.append("username", data.username);
        fd.append("name", data.name);
        if (data.avatar) fd.append("avatar", data.avatar);
        if (data.banner) fd.append("banner", data.banner);

        if (pb.authStore.model) {
            pb.collection("users")
                .update(pb.authStore.model?.id, fd)
                .then(() => {
                    setTimeout(() => {
                        setStatus("success");
                    }, 500);
                })
                .catch((error: ClientResponseError) => {
                    console.log(error.response);
                    setStatus("error");
                    if (error.response.data.username) {
                        setError(error.response.data.username.message);
                        return;
                    }
                    if (error.response.data.avatar) {
                        setError(error.response.data.avatar.message);
                        return;
                    }
                    setError(error?.response?.message);
                })
                .finally(() => {
                    setTimeout(() => {
                        setStatus("none");
                    }, 2000);
                });
        }
    }

    function removeImage(image: string, name: string) {
        const fd = new FormData();
        fd.append(image, "");
        if (pb.authStore.model) {
            pb.collection("users")
                .update(pb.authStore.model?.id, fd)
                .then(() => {
                    toast({
                        title: `👍 Removed ${name}`,
                        description: `Successfully removed your ${name}`
                    });
                });
        }
    }

    function changePassword() {
        pb.collection("users")
            .requestPasswordReset(pb.authStore.model?.email)
            .then(() => {
                toast({
                    title: `👍 Send!`,
                    description: `Email to change password has been send!`
                });
            });
    }

    return (
        <>
            <Helmet>
                <title>ReinVB Studio — Settings</title>
            </Helmet>
            <Container variant={"small"} className="my-8 lg:my-16">
                <h2 className="text-3xl font-bold">Settings.</h2>
                <div className="mt-8 w-full">
                    <ErrorMessage error={error} close={() => setError("")} />
                </div>
                <form className="mt-8 flex flex-col gap-6" onSubmit={handleSubmit(Save)}>
                    <div className="flex flex-col gap-2">
                        <h3 className="font-semibold">Username</h3>
                        <Input type="text" defaultValue={pb.authStore.model?.username} {...register("username")} />
                    </div>
                    <div className="flex flex-col gap-2">
                        <h3 className="font-semibold">Full name</h3>
                        <Input type="text" defaultValue={pb.authStore.model?.name} {...register("name")} />
                    </div>
                    <div className="flex flex-col gap-2">
                        <h3 className="font-semibold">Birthday</h3>
                        <Input className="" type="date" defaultValue={new Date(pb.authStore.model?.birthday)?.toISOString()?.substring(0, 10)} {...register("birthday")} />
                    </div>
                    <div className="flex flex-col gap-2">
                        <div className="flex justify-between">
                            <h3 className="font-semibold">Pick a new profile picture</h3>
                            <Button size={"extraSmall"} type="button" onClick={() => removeImage("avatar", "profile picture")}>
                                Remove
                            </Button>
                        </div>
                        <Input
                            className="file:text-slate-400 dark:file:text-slate-500"
                            id="small_size"
                            type="file"
                            onChange={(e) => {
                                const file = e.target.files?.[0]; // Access the first file from the FileList object
                                if (file) {
                                    setValue("avatar", file); // Assign the file to the "avatar" field in the form data using setValue
                                }
                            }}></Input>
                    </div>
                    <div className="flex flex-col gap-2">
                        <div className="flex justify-between">
                            <h3 className="font-semibold">Pick a new banner</h3>
                            <Button size={"extraSmall"} type="button" onClick={() => removeImage("banner", "banner")}>
                                Remove
                            </Button>
                        </div>
                        <Input
                            className="file:text-slate-400 dark:file:text-slate-500"
                            id="small_size"
                            type="file"
                            onChange={(e) => {
                                const file = e.target.files?.[0]; // Access the first file from the FileList object
                                if (file) {
                                    setValue("banner", file); // Assign the file to the "avatar" field in the form data using setValue
                                }
                            }}></Input>
                    </div>
                    <div>
                        <Button status={status} variant="dark" type="submit">
                            {status === "loading" && "Saving"}
                            {status === "success" && "Saved"}
                            {status === "error" && "Error"}
                            {status === "none" && "Save"}
                        </Button>
                    </div>
                </form>
                <hr className="my-8" />
                <h2 className="text-3xl font-bold">Other options.</h2>
                <p className="italic">Want to change your password?</p>

                <div className="mt-4 flex flex-col gap-2 md:flex-row">
                    <Confirmation title="Change password" description="A email will be send in order to change your password." onConfirm={changePassword}>
                        <Button className="w-full md:w-fit">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                            </svg>
                            Change password
                        </Button>
                    </Confirmation>
                    <ChangeEmail />
                </div>
            </Container>
        </>
    );
}
